import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { FIREBASE_ENABLED, getFirebase } from '../firebase';
import { getSession } from '../auth';
import type { BatchRecord, CourseRecord, AuditLog, StudentRecord, StudentBatchMembership, AttendanceRecord, LeadRecord, LeadAssignment } from '../domain';



export const isProductionData = () => FIREBASE_ENABLED;

function tenantIdOrThrow() {
  const tenantId = getSession()?.tenantId;
  if (!tenantId) throw new Error('No tenant is attached to this session.');
  return tenantId;
}

async function dbOrThrow() {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not enabled.');
  return fb.db as Parameters<typeof collection>[0];
}

function asIso(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate().toISOString();
  return new Date().toISOString();
}

export async function listCourses(): Promise<CourseRecord[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'courses'), orderBy('updatedAt', 'desc')));
    return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<CourseRecord, 'id'>), updatedAt: asIso(x.data().updatedAt) }));
  } catch {
    try {
      const snap = await getDocs(collection(db, 'tenants', tenantId, 'courses'));
      return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<CourseRecord, 'id'>), updatedAt: asIso(x.data().updatedAt) }));
    } catch { return []; }
  }
}

export async function getCourseById(courseId: string): Promise<CourseRecord | undefined> {
  const courses = await listCourses();
  return courses.find((course) => course.id === courseId);
}

export async function createCourse(input: Omit<CourseRecord, 'id' | 'tenantId' | 'createdBy' | 'updatedAt'>): Promise<string> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role || '')) throw new Error('Only the Owner/Admin can create courses.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const now = serverTimestamp();
  const cleaned: Record<string, unknown> = { tenantId, createdBy: session.uid, createdAt: now, updatedAt: now };
  for (const [k, v] of Object.entries(input)) if (v !== undefined && v !== null) cleaned[k] = v;
  const ref = await addDoc(collection(db, 'tenants', tenantId, 'courses'), cleaned);
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'course.created', entity: 'course', entityId: ref.id, after: cleaned, createdAt: now });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'course.created', entity: 'course', entityId: ref.id, after: input, createdAt: now });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return ref.id;
}

export async function listBatches(): Promise<BatchRecord[]> {
  const tenantId = tenantIdOrThrow();
  const session = getSession();
  const db = await dbOrThrow();
  const batchesRef = collection(db, 'tenants', tenantId, 'batches');
  const role = session?.role || session?.role;
  const isStaff = ['owner', 'admin', 'teamLead'].includes(role || '');
  try {
    // orderBy('startDate') fails the ENTIRE query if any batch doc lacks startDate.
    // Try ordered first, fall back to unordered so existing data always shows.
    const snap = isStaff
      ? await getDocs(query(batchesRef, orderBy('startDate', 'asc')))
      : await getDocs(query(batchesRef, where('educatorUids', 'array-contains', session?.uid || '')));
    return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<BatchRecord, 'id'>) }));
  } catch {
    try {
      const snap = await getDocs(batchesRef);
      return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<BatchRecord, 'id'>) }));
    } catch {
      return [];
    }
  }
}

export async function deleteCourse(courseId: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role || '')) throw new Error('Only the Owner/Admin can delete courses.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(doc(db, 'tenants', tenantId, 'courses', courseId));
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'course.deleted', entity: 'course', entityId: courseId, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function createBatch(input: Omit<BatchRecord, 'id' | 'tenantId'>): Promise<string> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role || '')) throw new Error('Only the Owner/Admin can create batches.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const cleaned: Record<string, unknown> = { tenantId, createdBy: session.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  for (const [k, v] of Object.entries(input)) if (v !== undefined && v !== null) cleaned[k] = v;
  const ref = await addDoc(collection(db, 'tenants', tenantId, 'batches'), cleaned);
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'batch.created', entity: 'batch', entityId: ref.id, after: input, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return ref.id;
}

export async function listStudentsForBatch(batchId: string): Promise<StudentRecord[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const memberships = await getDocs(query(collection(db, 'tenants', tenantId, 'studentBatchMemberships'), orderBy('joinedAt', 'asc')));
  const ids = memberships.docs.map((x) => x.data() as StudentBatchMembership).filter((x) => x.batchId === batchId && x.status === 'active').map((x) => x.studentUid);
  if (!ids.length) return [];
  const all = await getDocs(collection(db, 'tenants', tenantId, 'students'));
  return all.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<StudentRecord, 'id'>) })).filter((x) => ids.includes(x.id));
}

export async function addStudentToBatch(input: { batchId: string; name: string; email: string; phone: string }): Promise<string> {
  const session = getSession();
  if (!session || !['admin', 'owner'].includes(session.role)) throw new Error('Only the Owner/Admin can add learners to a batch.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const studentCleaned: Record<string, unknown> = { tenantId, name: input.name, status: 'active', createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  if (input.email) studentCleaned.email = input.email;
  if (input.phone) studentCleaned.phone = input.phone;
  const studentRef = await addDoc(collection(db, 'tenants', tenantId, 'students'), studentCleaned);
  const membershipRef = await addDoc(collection(db, 'tenants', tenantId, 'studentBatchMemberships'), { tenantId, studentUid: studentRef.id, batchId: input.batchId, joinedAt: serverTimestamp(), status: 'active' });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'student.added_to_batch', entity: 'studentBatchMembership', entityId: membershipRef.id, after: { studentId: studentRef.id, ...input }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return studentRef.id;
}

export async function listAttendanceForBatch(batchId: string, dates: string[]): Promise<AttendanceRecord[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'attendance'), where('batchId', '==', batchId)));
  return snap.docs
    .map((x) => ({ id: x.id, ...(x.data() as Omit<AttendanceRecord, 'id'>) }))
    .filter((r) => dates.includes(r.date));
}

export async function getTenantMembership(uid: string = getSession()?.uid || '') {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

export async function writeAttendance(record: Omit<AttendanceRecord, 'id' | 'tenantId' | 'markedByUid' | 'markedAt'>): Promise<{ id: string; writtenAt: number }> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !['owner', 'admin', 'teamLead', 'educator', 'instructor'].includes(role || '')) throw new Error('You do not have permission to mark attendance (role: ' + (role || 'none') + ').');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  if (['educator', 'instructor'].includes(role || '')) {
    const batchSnap = await getDoc(doc(db, 'tenants', tenantId, 'batches', record.batchId));
    const batch = batchSnap.data() as BatchRecord | undefined;
    if (!batch || !(batch.educatorUids || []).includes(session.uid)) throw new Error('You are not assigned to this batch.');
  }
  const id = `${record.batchId}_${record.studentUid}_${record.date}`;
  // Primary write — this is what makes attendance editable end-to-end.
  await setDoc(doc(db, 'tenants', tenantId, 'attendance', id), { ...record, tenantId, markedByUid: session.uid, markedAt: serverTimestamp() });
  // Audit log: best-effort. The rules for /auditLogs only allow isOwnerAdmin OR isTeamLead.
  // If the current user has an owner/admin role via tenant.ownerEmail but no
  // tenants/{tid}/users/{uid} doc with role:'owner', the auditLog write throws
  // permission-denied — we swallow it so the attendance write remains usable.
  try {
    try {
    await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'attendance.updated', entity: 'attendance', entityId: id, after: record, createdAt: serverTimestamp() });
    } catch (_e) { /* audit log may reject — primary write succeeded */ }
  } catch (_e) { /* audit log rules may reject non-listed members — that's OK */ }
  return { id, writtenAt: Date.now() };
}

export async function createLead(input: Omit<LeadRecord, 'id' | 'tenantId' | 'createdAt'>) {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !role || !['owner', 'admin', 'teamLead', 'salesAssociate'].includes(role)) throw new Error('Only sales, team lead, or admin users may create leads.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = await addDoc(collection(db, 'tenants', tenantId, 'leads'), { ...input, tenantId, createdAt: serverTimestamp() });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'lead.created', entity: 'lead', entityId: ref.id, after: input, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return ref.id;
}

export async function listLeads(): Promise<LeadRecord[]> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !role || !['owner', 'admin', 'teamLead', 'salesAssociate'].includes(role)) throw new Error('You do not have access to the lead pipeline.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = collection(db, 'tenants', tenantId, 'leads');
  const snap = ['owner', 'admin', 'teamLead'].includes(role)
    ? await getDocs(query(ref, orderBy('createdAt', 'desc')))
    : await getDocs(query(ref, where('ownerUid', '==', session.uid), orderBy('createdAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<LeadRecord, 'id'>), createdAt: asIso(x.data().createdAt) }));
}

export async function assignLead(leadId: string, salesAssociateUid: string): Promise<void> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !role || !['owner', 'admin', 'teamLead'].includes(role)) throw new Error('Only a Team Lead or Admin can assign leads.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'leads', leadId), { ownerUid: salesAssociateUid, assignedByUid: session.uid, status: 'assigned', updatedAt: serverTimestamp() }, { merge: true });
  const assignment: Omit<LeadAssignment, 'id'> = { tenantId, leadId, salesAssociateUid, assignedByUid: session.uid, assignedAt: new Date().toISOString() };
  await addDoc(collection(db, 'tenants', tenantId, 'leadAssignments'), assignment);
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'lead.assigned', entity: 'lead', entityId: leadId, after: assignment, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function updateLeadStatus(leadId: string, status: LeadRecord['status']): Promise<void> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !role || !['owner', 'admin', 'teamLead', 'salesAssociate'].includes(role)) throw new Error('You do not have access to update this lead.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'leads', leadId), { status, updatedAt: serverTimestamp() }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'lead.status_changed', entity: 'lead', entityId: leadId, after: { status }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function listCommerceOrders(): Promise<import('../commerce').CommerceOrder[]> {
  const session = getSession();
  if (!session) throw new Error('You must be signed in to view commerce orders.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const role = session.role;
  const ref = collection(db, 'tenants', tenantId, 'commerceOrders');
  const snap = ['owner', 'admin'].includes(role || '')
    ? await getDocs(query(ref, orderBy('createdAt', 'desc')))
    : await getDocs(query(ref, where('studentUid', '==', session.uid), orderBy('createdAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<import('../commerce').CommerceOrder, 'id'>), createdAt: asIso(x.data().createdAt) }));
}

export async function updateCommerceOrder(orderId: string, patch: Partial<Pick<import('../commerce').CommerceOrder, 'sellingPrice' | 'confirmationStatus' | 'addressConfirmed' | 'confirmedAt' | 'confirmedByUid' | 'deliveryStatus'>>): Promise<void> {
  const session = getSession();
  if (!session) throw new Error('You must be signed in to update an order.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = doc(db, 'tenants', tenantId, 'commerceOrders', orderId);
  await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'commerce.order_updated', entity: 'commerceOrder', entityId: orderId, after: patch, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function createSettlementSnapshot(order: import('../commerce').CommerceOrder): Promise<string> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !['owner', 'admin'].includes(role || '')) throw new Error('Only Owner/Admin can settle an order.');
  if (order.deliveryStatus !== 'delivered') throw new Error('Only delivered orders can be settled.');
  const settlement = (await import('../commerce')).calculateSettlement(order);
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = await addDoc(collection(db, 'tenants', tenantId, 'commerceSettlements'), { tenantId, orderId: order.id, studentUid: order.studentUid, ...settlement, deliveryStatus: 'delivered', settledAt: serverTimestamp() });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'commerce.settlement_created', entity: 'commerceSettlement', entityId: ref.id, after: settlement, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return ref.id;
}

export async function requestPayout(frequency: import('../commerce').PayoutFrequency, summary: import('../commerce').PayoutSummary): Promise<string> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || role !== 'learner') throw new Error('Only a student seller can request their own payout.');
  if (summary.payableAmount <= 0) throw new Error('There is no payable balance in this payout period.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = await addDoc(collection(db, 'tenants', tenantId, 'commercePayouts'), { tenantId, studentUid: session.uid, frequency, summary, status: 'requested', requestedAt: serverTimestamp() });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'commerce.payout_requested', entity: 'commercePayout', entityId: ref.id, after: { frequency, summary }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return ref.id;
}

export async function getCommerceProfile(studentUid?: string) {
  const session = getSession();
  if (!session) throw new Error('You must be signed in to view commerce settings.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const uid = studentUid || session.uid;
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'commerceProfiles', uid));
  return snap.exists() ? snap.data() as { studentUid: string; payoutFrequency: import('../commerce').PayoutFrequency; bankAccountStatus?: string; reinvestmentTarget?: number } : null;
}

export async function saveCommerceProfile(studentUid: string, payoutFrequency: import('../commerce').PayoutFrequency, reinvestmentTarget = 0) {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !role || (!['owner', 'admin'].includes(role) && !(role === 'learner' && studentUid === session.uid))) throw new Error('You do not have permission to change this payout profile.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'commerceProfiles', studentUid), { tenantId, studentUid, payoutFrequency, reinvestmentTarget, updatedAt: serverTimestamp() }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'commerce.profile_updated', entity: 'commerceProfile', entityId: studentUid, after: { studentUid, payoutFrequency, reinvestmentTarget }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function listPayoutRequests(): Promise<import('../commerce').PayoutRequest[]> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !['owner', 'admin'].includes(role || '')) throw new Error('Only the Owner/Admin can view payout requests.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'commercePayouts'), orderBy('requestedAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<import('../commerce').PayoutRequest, 'id'>), requestedAt: asIso(x.data().requestedAt) }));
}

export async function listCommerceSettlements(): Promise<import('../commerce').SettlementSnapshot[]> {
  const session = getSession();
  const role = session?.role || session?.role;
  if (!session || !['owner', 'admin'].includes(role || '')) throw new Error('Only the Owner/Admin can view settlements.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'commerceSettlements'), orderBy('settledAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<import('../commerce').SettlementSnapshot, 'id'>), settledAt: asIso(x.data().settledAt) }));
}

export async function approvePayout(payoutId: string, approve: boolean): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes((session.role) || '')) throw new Error('Only the Owner/Admin can approve payouts.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'commercePayouts', payoutId), { status: approve ? 'approved' : 'failed', decidedByUid: session.uid, decidedAt: serverTimestamp() }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: approve ? 'commerce.payout_approved' : 'commerce.payout_withheld', entity: 'commercePayout', entityId: payoutId, after: { status: approve ? 'approved' : 'failed' }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function listManufacturerSettlements(): Promise<import('../commerce').ManufacturerSettlement[]> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes((session.role) || '')) throw new Error('Only the Owner/Admin can view manufacturer settlements.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'manufacturerSettlements'), orderBy('createdAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<import('../commerce').ManufacturerSettlement, 'id'>) }));
}

export async function markManufacturerSettled(settlementId: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes((session.role) || '')) throw new Error('Only the Owner/Admin can settle the manufacturer.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'manufacturerSettlements', settlementId), { status: 'paid', paidAt: serverTimestamp(), settledByUid: session.uid }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'commerce.manufacturer_settled', entity: 'manufacturerSettlement', entityId: settlementId, after: { status: 'paid' }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function listSiteContent(): Promise<import('../site-content').SiteCourseListing[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'siteContent'), orderBy('updatedAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<import('../site-content').SiteCourseListing, 'id'>), updatedAt: asIso(x.data().updatedAt) }));
}

export async function saveSiteContent(listing: Omit<import('../site-content').SiteCourseListing, 'id' | 'tenantId' | 'updatedAt'>, id?: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes((session.role) || '')) throw new Error('Only the Owner/Admin can edit website content.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = id ? doc(db, 'tenants', tenantId, 'siteContent', id) : doc(collection(db, 'tenants', tenantId, 'siteContent'));
  await setDoc(ref, { ...listing, tenantId, updatedAt: serverTimestamp() }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'siteContent.saved', entity: 'siteContent', entityId: ref.id, after: { ...listing, status: listing.status }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function listSiteSections(): Promise<import('../site-content').SiteSection[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(collection(db, 'tenants', tenantId, 'siteContent'));
  return snap.docs
    .map((x) => ({ id: x.id, ...(x.data() as Omit<import('../site-content').SiteSection, 'id'>), updatedAt: asIso(x.data().updatedAt) }))
    .filter((d): d is import('../site-content').SiteSection => !!(d as { type?: string }).type && typeof (d as { type?: string }).type === 'string');
}

export async function saveSiteSection(section: import('../site-content').SiteSection, id: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes((session.role) || '')) throw new Error('Only the Owner/Admin can edit website content.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'siteContent', id), { ...section, tenantId, updatedAt: serverTimestamp() }, { merge: true });
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'siteContent.section_saved', entity: 'siteContent', entityId: id, after: { type: section.type, status: section.status }, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
}

export async function listStudents(): Promise<StudentRecord[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'students'), orderBy('createdAt', 'desc')));
  return snap.docs.map((x) => ({ id: x.id, tenantId, ...(x.data() as Omit<StudentRecord, 'id' | 'tenantId'>) }));
}

export async function createStudent(input: { name: string; email?: string; phone?: string; course?: string }): Promise<string> {
  const session = getSession();
  if (!session || !['owner', 'admin', 'teamLead'].includes(session.role)) throw new Error('Only owners/admins/team-leads can add students.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const cleaned: Record<string, unknown> = { tenantId, name: input.name, status: 'active', course: input.course || '', createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  if (input.email) cleaned.email = input.email;
  if (input.phone) cleaned.phone = input.phone;
  const ref = await addDoc(collection(db, 'tenants', tenantId, 'students'), cleaned);
  try {
  await addDoc(collection(db, 'tenants', tenantId, 'auditLogs'), { tenantId, actorUid: session.uid, action: 'student.created', entity: 'student', entityId: ref.id, after: cleaned, createdAt: serverTimestamp() });
  } catch (_e) { /* audit log may reject — primary write succeeded */ }
  return ref.id;
}

export interface MemberRow { id: string; role: string; email: string; name: string; }

export async function listMembers(): Promise<MemberRow[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDocs(collection(db, 'tenants', tenantId, 'users'));
  return snap.docs.map((x) => ({ id: x.id, ...(x.data() as Omit<MemberRow, 'id'>) }));
}

export async function updateMemberRole(uid: string, role: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role)) throw new Error('Only owners/admins can change roles.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'users', uid), { role }, { merge: true });
}

export async function listPayrollForWeek(): Promise<Array<{ id: string; employeeUid: string; employeeName: string; role?: string; hours?: number; net?: number; gross?: number }>> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role)) return [];
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'payroll'), orderBy('createdAt', 'desc')));
  return snap.docs.slice(0, 50).map((d) => {
    const data = d.data() as Record<string, unknown>;
    return {
      id: d.id,
      employeeUid: (data.employeeUid as string) || d.id,
      employeeName: (data.employeeName as string) || (data.name as string) || 'Staff',
      role: data.role as string | undefined,
      hours: typeof data.hours === 'number' ? data.hours : Number(data.hours) || 0,
      net: typeof data.net === 'number' ? data.net : Number(data.net) || 0,
      gross: typeof data.gross === 'number' ? data.gross : Number(data.gross) || 0,
    };
  });
}

export async function listSettlements(): Promise<Array<{ id: string; settlementAmount?: number; netAmount?: number; orderId?: string }>> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role)) return [];
  const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'commerceSettlements'), orderBy('settledAt', 'desc')));
  return snap.docs.slice(0, 100).map((d) => {
    const data = d.data() as Record<string, unknown>;
    return {
      id: d.id,
      settlementAmount: typeof data.settlementAmount === 'number' ? data.settlementAmount : Number(data.settlementAmount) || Number(data.amount) || 0,
      netAmount: typeof data.netAmount === 'number' ? data.netAmount : Number(data.netAmount) || 0,
      orderId: data.orderId as string | undefined,
    };
  });
}

export type CourseMaterialType = 'video' | 'quiz' | 'note';
export interface CourseMaterial {
  id: string;
  type: CourseMaterialType;
  title: string;
  url?: string;
  note?: string;
  createdAt?: string;
  createdBy?: string;
  order?: number;
}

export async function getCourse(courseId: string): Promise<{ id: string; title: string; code?: string; durationWeeks?: number; level?: string; status?: string; description?: string } | null> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'courses', courseId));
  return snap.exists() ? ({ id: courseId, ...(snap.data() as Record<string, unknown>) }) : null;
}

export async function listCourseMaterials(courseId: string): Promise<CourseMaterial[]> {
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'courses', courseId, 'materials'), orderBy('createdAt', 'desc')));
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CourseMaterial, 'id'>) }));
  } catch {
    try {
      const snap = await getDocs(collection(db, 'tenants', tenantId, 'courses', courseId, 'materials'));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CourseMaterial, 'id'>) }));
    } catch { return []; }
  }
}

export async function createCourseMaterial(courseId: string, payload: { type: CourseMaterialType; title: string; url?: string; note?: string }): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin', 'teamLead', 'educator'].includes(session.role)) throw new Error('Only Owner/Admin/TeamLead/Educator can add course content.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await addDoc(collection(db, 'tenants', tenantId, 'courses', courseId, 'materials'), {
    ...payload,
    url: payload.url || '',
    note: payload.note || '',
    createdAt: new Date().toISOString(),
    createdBy: session.uid,
  });
}

export async function deleteCourseMaterial(courseId: string, materialId: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin', 'teamLead', 'educator'].includes(session.role)) throw new Error('Only Owner/Admin/TeamLead/Educator can delete course content.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(doc(db, 'tenants', tenantId, 'courses', courseId, 'materials', materialId));
}

export interface LeadAssignee { uid: string; name: string; role: string; }



export interface LeadAssignee { uid: string; name: string; role: string; }
export async function listLeadAssignableMembers(): Promise<LeadAssignee[]> {
  const session = getSession();
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  const ref = collection(db, 'tenants', tenantId, 'users');
  let snap;
  try { snap = await getDocs(query(ref, where('role', 'in', ['owner','admin','teamLead','salesAssociate']))); } catch { snap = await getDocs(ref); }
  const accepted = new Set(['owner','admin','teamLead','salesAssociate']);
  const out: LeadAssignee[] = [];
  for (const d of snap.docs) {
    const data = d.data();
    if (!accepted.has(data.role)) continue;
    out.push({ uid: d.id, name: data.name || data.displayName || data.email || d.id, role: data.role });
  }
  if (session && !out.some((m) => m.uid === session.uid)) {
    out.unshift({ uid: session.uid, name: session.name || 'Me', role: session.role || 'teamLead' });
  }
  return out.length ? out : (session ? [{ uid: session.uid, name: session.name || 'Me', role: session.role || 'teamLead' }] : []);
}

export async function updateStudentRecord(studentId: string, patch: Partial<Pick<StudentRecord, 'name' | 'email' | 'phone' | 'status'>>): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin', 'teamLead'].includes(session.role || '')) throw new Error('Only Owner/Admin/TeamLead can edit students.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await setDoc(doc(db, 'tenants', tenantId, 'students', studentId), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}
export async function deleteStudentRecord(studentId: string): Promise<void> {
  const session = getSession();
  if (!session || !['owner', 'admin', 'teamLead'].includes(session.role || '')) throw new Error('Only Owner/Admin/TeamLead can remove students.');
  const tenantId = tenantIdOrThrow();
  const db = await dbOrThrow();
  await deleteDoc(doc(db, 'tenants', tenantId, 'students', studentId));
}

// deleteDoc import guard
